import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { MeResponse } from '../../../shared/types';
import { Icon, type IconName } from './icons';
import { Logo } from './Logo';
import type { Theme } from './theme';
import { Avatar } from './ui';

export interface NavItem {
  key: string;
  icon: IconName;
  label: string;
  ping?: boolean;
  /** Sezione della barra laterale (titolo visibile quando è allargata). */
  group?: string;
  /** Voce che raccoglie più pagine (mostrate come schede in alto): la voce è attiva su ognuna di esse. */
  pages?: string[];
  /** Pagine della voce che non hanno una scheda in alto (si aprono da altre pagine, es. Rubrica dalla scheda azienda). */
  hidden?: string[];
  /** Linea di separazione prima della voce (es. «Altro» in fondo). */
  sep?: boolean;
  /** Colore della voce: icona colorata e linea a sinistra quando è aperta (ci si abitua e si trova prima). */
  color?: string;
}

/** Scheda in alto di una voce del menu che raccoglie più pagine. */
export interface SectionTab {
  key: string;
  label: string;
}

const ROLE_LABEL: Record<MeResponse['user']['role'], string> = {
  admin: 'Consulente',
  agent: 'Commerciale',
  client: 'Cliente',
};

interface Props {
  nav: NavItem[];
  page: string;
  onNavigate: (key: string) => void;
  /** Tasto principale: nuova segnalazione (inizio del percorso cliente) */
  onNewLead?: () => void;
  me: MeResponse;
  live: boolean;
  version: string;
  updateReady: string | null;
  onInstallUpdate: () => void;
  onLogout: () => void;
  /** Notifiche non lette (numero sulla campanella) */
  unread: number;
  theme: Theme;
  onToggleTheme: () => void;
  /** Campanella: apre le notifiche o torna alla sezione di prima */
  onToggleNotices: () => void;
  /** Voce attiva del menu e sue schede (se ne ha più di una) */
  section?: { label: string; tabs: SectionTab[]; color?: string } | null;
  /** Apre la ricerca unica (anche con Ctrl+K) */
  onSearch?: () => void;
  children: React.ReactNode;
}

const isActive = (item: NavItem, page: string) => page === item.key || !!item.pages?.includes(page);

declare const __BUILD_TIME__: string;
/** Giorno e ora in cui è stata preparata questa versione (ora italiana). */
function buildStamp(): string {
  try {
    return new Date(__BUILD_TIME__).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome' }).replace(',', '');
  } catch {
    return '';
  }
}

/** Struttura fissa dell'app: sidebar a icone, barra superiore, area di lavoro. */
export function Shell({ nav, page, onNavigate, onNewLead, me, live, version, updateReady, onInstallUpdate, onLogout, unread, theme, onToggleTheme, onToggleNotices, section, onSearch, children }: Props) {
  const area = ROLE_LABEL[me.user.role];
  const navsRef = useRef<HTMLElement>(null);
  // Barra laterale: solo icone o allargata con i nomi delle sezioni (scelta ricordata)
  const [wide, setWide] = useState(() => localStorage.getItem('tt.sidebarWide') === '1');
  const toggleWide = () =>
    setWide((w) => {
      localStorage.setItem('tt.sidebarWide', w ? '0' : '1');
      return !w;
    });
  // Selettore bianco unico che scorre da una voce all'altra: posizione e misura sono quelle della voce attiva.
  const [indicator, setIndicator] = useState({ x: 0, y: 0, w: 48, h: 48, visible: false });
  const activeColor = nav.find((n) => isActive(n, page))?.color;

  useLayoutEffect(() => {
    const navs = navsRef.current;
    if (!navs) return;
    const update = () => {
      const active = navs.querySelector<HTMLElement>('.nav.active');
      if (active) {
        setIndicator({ x: active.offsetLeft, y: active.offsetTop, w: active.offsetWidth, h: active.offsetHeight, visible: true });
        return;
      }
      // Sulla dashboard il selettore "rientra" nel riquadro scuro in alto e sparisce.
      const tile = navs.parentElement?.querySelector<HTMLElement>('.tile');
      setIndicator((i) => ({ ...i, y: tile ? tile.offsetTop - navs.offsetTop : 0, visible: false }));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(navs);
    navs.querySelectorAll('.nav').forEach((n) => ro.observe(n));
    return () => ro.disconnect();
  }, [page, nav.length, wide]);

  return (
    <div className={`shell${wide ? ' wide' : ''}`}>
      <aside className="sidebar">
        <button type="button" className="side-toggle" title={wide ? 'Riduci la barra' : 'Allarga la barra'} onClick={toggleWide}>
          <Icon name={wide ? 'chevronLeft' : 'chevronRight'} size={14} />
        </button>
        <div className="side-head">
          <button type="button" className="tile logo-tile" title="Torna a «Oggi»" onClick={() => onNavigate('dashboard')}>
            <Logo size={38} />
          </button>
          <span className="side-brand">
            <b>CRM</b> TecnoSide
          </span>
        </div>
        {onNewLead && (
          <button type="button" className="side-new" title="Nuova segnalazione: un possibile cliente da contattare" onClick={onNewLead}>
            <Icon name="plus" size={20} />
            <span className="nav-label">Nuova segnalazione</span>
          </button>
        )}
        <nav className="navs" ref={navsRef}>
          <span
            className="nav-indicator"
            style={{
              transform: `translate(${indicator.x}px, ${indicator.y}px)`,
              width: indicator.w,
              height: indicator.h,
              opacity: indicator.visible ? 1 : 0,
              ...(activeColor ? ({ '--nc': activeColor } as React.CSSProperties) : {}),
            }}
            data-colored={activeColor ? '1' : undefined}
          />
          {nav.map((item, i) => {
            const newGroup = item.group && item.group !== nav[i - 1]?.group;
            return (
              <React.Fragment key={item.key}>
                {newGroup && <div className={`nav-group${i === 0 ? ' first' : ''}`}>{item.group}</div>}
                {item.sep && <div className="nav-sep" />}
                <button
                  type="button"
                  className={`nav${isActive(item, page) ? ' active' : ''}${item.color ? ' colored' : ''}`}
                  style={item.color ? ({ '--nc': item.color } as React.CSSProperties) : undefined}
                  title={wide ? undefined : item.label}
                  onClick={() => onNavigate(item.key)}
                >
                  {item.color ? (
                    <span className="nav-ic">
                      <Icon name={item.icon} size={18} />
                    </span>
                  ) : (
                    <Icon name={item.icon} size={22} />
                  )}
                  <span className="nav-label">{item.label}</span>
                  {item.ping && <span className="ping" />}
                </button>
              </React.Fragment>
            );
          })}
        </nav>
        <span className="spacer" />
        <div className="side-version" title={`Versione ${version}
Preparata il ${buildStamp()}`}>
          <b>v{version}</b>
          <span>{buildStamp()}</span>
        </div>
      </aside>

      <div className="workspace">
        <header className="topbar">
          {section ? (
            <>
              <div className={`crumb${section.color ? ' colored' : ''}`} style={section.color ? ({ '--nc': section.color } as React.CSSProperties) : undefined}>
                <b>{section.label}</b>
              </div>
              {section.tabs.length > 1 && (
                <div className="sec-tabs" role="tablist" style={section.color ? ({ '--nc': section.color } as React.CSSProperties) : undefined}>
                  {section.tabs.map((t) => (
                    <button key={t.key} type="button" role="tab" aria-selected={page === t.key} className={page === t.key ? 'on' : ''} onClick={() => onNavigate(t.key)}>
                      {t.label}
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="crumb">
              Area <b>{area}</b>
            </div>
          )}
          <div className="tools">
            {/* Si vede solo quando c'è un problema (23/10/2026, Marco): da collegati non serve */}
            {!live && (
              <span className="tool dead tool-offline" title="Il programma non riceve gli avvisi finché non si ricollega. Si ricollega da solo.">
                <Icon name="wifiOff" size={18} />
                <span>Collegamento al CRM interrotto: mi ricollego…</span>
              </span>
            )}
            {updateReady && (
              <span className="notice-pill">
                ver. {updateReady} disponibile
                <button type="button" className="btn small dark" title="Chiude e riapre il CRM con la versione nuova: pochi secondi" onClick={onInstallUpdate}>
                  {/Mac/i.test(navigator.platform) ? 'Scarica' : 'Riavvia'}
                </button>
              </span>
            )}
          </div>
          <div className="right">
            {onSearch && (
              <button type="button" className="top-search" title="Cerca in tutto il CRM (Ctrl+K)" onClick={onSearch}>
                <Icon name="search" size={16} />
                <span className="ts-label">Cerca</span>
                <kbd className="ts-kbd">Ctrl K</kbd>
              </button>
            )}
            <button type="button" className="top-btn top-theme" title={theme === 'dark' ? 'Passa al tema chiaro' : 'Passa al tema scuro'} onClick={onToggleTheme}>
              <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={19} />
            </button>
            <button
              type="button"
              className={`top-btn${page === 'notices' ? ' on' : ''}`}
              title={unread > 0 ? `Notifiche (${unread} nuove)` : 'Notifiche'}
              onClick={onToggleNotices}
            >
              <Icon name="bell" size={19} />
              {unread > 0 && <span className="top-badge">{unread > 99 ? '99+' : unread}</span>}
            </button>
            <UserMenu me={me} area={area} hasSettings={nav.some((n) => n.key === 'settings' || !!n.pages?.includes('settings'))} onSettings={() => onNavigate('settings')} onLogout={onLogout} />
          </div>
        </header>
        <div key={page} className="page">
          {children}
        </div>
      </div>
    </div>
  );
}

/** Utente in alto a destra: nome e, al clic, un menu con Impostazioni ed Esci (come nelle app più diffuse). */
function UserMenu({ me, area, hasSettings, onSettings, onLogout }: { me: MeResponse; area: string; hasSettings: boolean; onSettings: () => void; onLogout: () => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pick = (fn: () => void) => () => {
    setOpen(false);
    fn();
  };

  return (
    <div className="user-menu" ref={ref}>
      <button type="button" className={`user-btn${open ? ' on' : ''}`} title="Il tuo account: impostazioni ed esci" onClick={() => setOpen((o) => !o)}>
        <Avatar name={me.user.name} size={28} />
        <span className="user-name">{me.user.name}</span>
        <Icon name="chevronDown" size={14} />
      </button>
      {open && (
        <div className="ctx-menu user-pop" role="menu">
          <div className="user-pop-head">
            <Avatar name={me.user.name} size={38} />
            <div style={{ minWidth: 0 }}>
              <b>{me.user.name}</b>
              <div className="muted small" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{me.user.email}</div>
              <div className="muted tiny">
                {area}
                {me.clientName ? ` · ${me.clientName}` : ''}
              </div>
            </div>
          </div>
          {hasSettings && (
            <button type="button" role="menuitem" className="ctx-item" onClick={pick(onSettings)}>
              <Icon name="gear" size={17} /> Impostazioni
            </button>
          )}
          <button type="button" role="menuitem" className="ctx-item danger" onClick={pick(onLogout)}>
            <Icon name="logout" size={17} /> Esci
          </button>
        </div>
      )}
    </div>
  );
}
